import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, ArrowRight, Loader2, Send, CheckCircle2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegistrationModalContent = ({ event, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/events/${event.id}/register`, form);
      setDone(true);
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail?.[0]?.msg || "Veuillez réessayer." });
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="py-4 text-center" data-testid="event-register-success">
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 180 }}
          className="w-14 h-14 bg-navy-500 text-white flex items-center justify-center mx-auto mb-5"
        >
          <CheckCircle2 className="h-7 w-7" />
        </motion.div>
        <div className="overline mb-3">Inscription confirmée</div>
        <h3 className="font-display text-2xl md:text-3xl text-navy-700 mb-3">À très vite, {form.name.split(" ")[0]} !</h3>
        <p className="text-[#555] text-[14px] mb-6">
          Un email de confirmation a été envoyé à <strong className="text-navy-700">{form.email}</strong>.
        </p>
        <button onClick={onClose} className="btn-outline-navy" data-testid="event-register-close">Fermer</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="event-register-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="er-name" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Nom *</Label>
          <Input id="er-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="event-input-name" />
        </div>
        <div>
          <Label htmlFor="er-email" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Email *</Label>
          <Input id="er-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="event-input-email" />
        </div>
        <div>
          <Label htmlFor="er-phone" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Téléphone</Label>
          <Input id="er-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+225 ..."
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="event-input-phone" />
        </div>
        <div>
          <Label htmlFor="er-org" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Organisation</Label>
          <Input id="er-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
            className="mt-2 rounded-none border-0 border-b border-navy-100 px-0 h-11 focus-visible:ring-0 focus-visible:border-navy-500"
            data-testid="event-input-org" />
        </div>
      </div>
      <div>
        <Label htmlFor="er-msg" className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Message (optionnel)</Label>
        <Textarea id="er-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="mt-2 rounded-none border-navy-100 focus-visible:ring-navy-500 focus-visible:ring-offset-0 focus-visible:border-navy-500 resize-none"
          data-testid="event-input-message" />
      </div>
      <button type="submit" disabled={loading} className="btn-red w-full md:w-auto disabled:opacity-60" data-testid="event-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Envoi…" : "Confirmer mon inscription"}
      </button>
    </form>
  );
};

const EventCard = ({ ev, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: idx * 0.1 }}
      className="bg-white border border-navy-100 hover-rise flex flex-col group"
      data-testid={`event-card-${ev.id}`}
    >
      {ev.image_url ? (
        <div className="relative aspect-[16/9] overflow-hidden zoom-frame">
          <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
          {ev.category && (
            <div className="absolute top-4 left-4 px-2.5 py-1 bg-white/90 backdrop-blur text-navy-700 text-[10px] uppercase tracking-[0.18em] font-semibold">
              {ev.category}
            </div>
          )}
        </div>
      ) : (
        <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="none">
            <defs>
              <pattern id={`ev-pat-${ev.id}`} width="14" height="14" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#ev-pat-${ev.id})`} />
          </svg>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-500/30 blur-3xl rounded-full" />
          <div className="relative text-center">
            <Sparkles className="h-7 w-7 text-red-500 mx-auto mb-2 fill-red-500/50" strokeWidth={1.4} />
            <CalendarDays className="h-10 w-10 text-white mx-auto mb-2" strokeWidth={1.2} />
            <div className="text-white text-[11px] uppercase tracking-[0.22em] font-semibold">
              {ev.category || "Événement"}
            </div>
          </div>
          {/* Tricolor accent stripe */}
          <div className="absolute bottom-0 left-0 right-0 h-[3px] flex">
            <span className="flex-1 bg-[#002362]" />
            <span className="flex-1 bg-white" />
            <span className="flex-1 bg-[#ED2939]" />
          </div>
        </div>
      )}
      <div className="p-7 flex flex-col flex-1">
        {ev.category && !ev.image_url && (
          <div className="overline text-navy-500 mb-3">{ev.category}</div>
        )}
        <h3 className="font-display text-2xl text-navy-700 leading-tight mb-3">{ev.title}</h3>
        <p className="text-[#555] text-[14px] leading-relaxed mb-5 flex-1 line-clamp-3">{ev.description}</p>
        <ul className="space-y-2 text-[13px] text-navy-700 border-t border-navy-100 pt-4 mb-5">
          <li className="flex items-center gap-2.5"><CalendarDays className="h-4 w-4 text-navy-500" />{ev.event_date}</li>
          <li className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-navy-500" />{ev.location}</li>
          {ev.capacity != null && (
            <li className="flex items-center gap-2.5"><Users className="h-4 w-4 text-navy-500" />{ev.capacity} places</li>
          )}
        </ul>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-red w-full" data-testid={`event-register-button-${ev.id}`}>
              Je m'inscris
              <ArrowRight className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none bg-white border border-navy-100">
            <DialogHeader>
              <div className="overline text-navy-500 mb-1">Inscription</div>
              <DialogTitle className="font-display text-2xl md:text-3xl text-navy-700">{ev.title}</DialogTitle>
              <DialogDescription className="text-[#666]">
                {ev.event_date} · {ev.location}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <RegistrationModalContent event={ev} onClose={() => setOpen(false)} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </motion.div>
  );
};

export const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setEvents(Array.isArray(r.data) ? r.data : [])).catch(() => setEvents([])).finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  return (
    <section id="events" data-testid="events-section" className="section-padding bg-[#FAF8F3]">
      <div className="container-edge">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="overline mb-4">Agenda</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
              Nos prochains <em className="not-italic red-shine">événements</em>.
            </h2>
          </div>
          <p className="text-[#555] max-w-md text-[15px] leading-relaxed">
            Portes ouvertes, conférences, ateliers, soirées Alumni — venez vivre ECL avant d'y entrer.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#888]">Chargement des événements…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev, i) => <EventCard key={ev.id} ev={ev} idx={i} />)}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
